'use strict';

/**
 * shared/graph/ontology.js — THE ONE canonical relationship-type vocabulary.
 *
 * WHY THIS EXISTS (consolidation U11)
 *   The controlled 9-type edge vocabulary — its type strings, forward/inverse display
 *   labels, and symmetry flags — was hand-mirrored across FOUR runtime copies:
 *     - backend `src/mcp/lib/graph-ontology.mjs` (the ONTOLOGY object)
 *     - backend `src/mcp/lib/relationship-graph.mjs` (FORWARD/INVERSE maps)
 *     - backend `src/utils/graph-background-block.js` (FORWARD/INVERSE maps)
 *     - frontend `src/lib/relationship-adapter.ts` (FORWARD/INVERSE maps)
 *   plus the `VALID_TYPES` list in `src/lib/relationship-service.ts`. Five mirrors of one
 *   locked vocab, drift-guarded only by brittle source-parsing tests. This module is the
 *   single source all runtimes consume — plain CJS, ZERO requires, importable by the
 *   Node/ESM backend AND Next.js TS via the `@shared/*` alias + a `.d.ts` sidecar, exactly
 *   like `shared/publish/transitions.js`.
 *
 * PURITY CONTRACT
 *   Requires NOTHING. Pure data + pure lookups. FORWARD/INVERSE and VALID_TYPES are DERIVED
 *   from ONTOLOGY — there is only ONE authored vocabulary.
 *
 * GROWTH
 *   Adding a type is a code change here + review, never free-text. Types flagged
 *   `requiresDescription:true` MUST carry a free-text description — for those, the prose
 *   IS the edge (enforced at the write paths).
 *
 * RUNTIME OVERRIDE
 *   A Firestore `graph_ontology/current` doc can override this at runtime (seeded by
 *   `backend/scripts/seed-graph-ontology.mjs`). The in-code ONTOLOGY is the canonical
 *   fallback/seed.
 */

/**
 * @typedef {{ type: string, forwardLabel: string, inverseLabel: string, symmetric: boolean, direction?: string, roleHint?: string, requiresDescription?: boolean }} OntologyType
 * @typedef {{ version: string, description: string, types: OntologyType[] }} Ontology
 */

/**
 * Canonical in-code ontology (the 9-type vocab locked 2026-06-07).
 *
 * Fields per type:
 *   type         — the controlled enum string stored in entity_relationships docs
 *   forwardLabel — display label when traversing FROM the source (e.g. "Luca founded …")
 *   inverseLabel — display label when traversing TO the source (e.g. "Pudgy Penguins founded by …")
 *   symmetric    — when true, forwardLabel === inverseLabel (PARTNER_OF, ASSOCIATED_WITH)
 *   direction    — canonical "from entity type → to entity type" hint (informational, not enforced)
 *   roleHint     — example role values that make sense for this type (informational)
 *   requiresDescription — the mandatory free-text narrative flag (ASSOCIATED_WITH)
 */
const ONTOLOGY = {
  version: '1.2.0',
  description: 'Controlled vocabulary for entity_relationships edges in the Descout graph. ' +
    'Agents must only propose edges whose type appears in this list. Types flagged ' +
    'requiresDescription:true MUST carry a free-text description — for those, the prose IS the edge.',
  types: [
    {
      type: 'FOUNDED',
      forwardLabel: 'founded',
      inverseLabel: 'founded by',
      symmetric: false,
      direction: 'person → org',
      roleHint: 'CEO, co-founder, technical co-founder',
    },
    {
      type: 'WORKED_AT',
      forwardLabel: 'worked at',
      inverseLabel: 'team includes',
      symmetric: false,
      direction: 'person → org',
      roleHint: 'CTO, art lead, engineer, advisor',
    },
    {
      type: 'SUBSIDIARY_OF',
      forwardLabel: 'subsidiary of',
      inverseLabel: 'operates',
      symmetric: false,
      direction: 'org → org',
    },
    {
      type: 'MASCOT_OF',
      forwardLabel: 'mascot of',
      inverseLabel: 'has mascot',
      symmetric: false,
      direction: 'entity → project/org',
    },
    {
      type: 'INSPIRED_BY',
      forwardLabel: 'inspired by',
      inverseLabel: 'inspiration for',
      symmetric: false,
    },
    {
      type: 'PARTNER_OF',
      forwardLabel: 'partner of',
      inverseLabel: 'partner of',
      symmetric: true,
    },
    {
      type: 'BUILT_ON',
      forwardLabel: 'built on',
      inverseLabel: 'powers',
      symmetric: false,
      direction: 'project → chain/platform',
    },
    {
      type: 'DEVELOPED_BY',
      forwardLabel: 'developed by',
      inverseLabel: 'developer of',
      symmetric: false,
      direction: 'product/game → studio/org',
      roleHint: 'game studio, dev shop, publisher',
    },
    {
      type: 'ASSOCIATED_WITH',
      forwardLabel: 'associated with',
      inverseLabel: 'associated with',
      symmetric: true,
      requiresDescription: true,
    },
  ],
};

// ── Derived label maps (single source: ONTOLOGY). `{ TYPE: label }`. ─────────────────
// DERIVED, not authored — the grep invariant "exactly one vocabulary definition" holds.
const FORWARD = {};
const INVERSE = {};
for (const t of ONTOLOGY.types) {
  FORWARD[t.type] = t.forwardLabel;
  INVERSE[t.type] = t.inverseLabel;
}

/** Forward display label for a type; falls back to 'related to' for an unknown type. */
function forwardLabel(type) { return FORWARD[type] || 'related to'; }
/** Inverse display label for a type; falls back to 'related to' for an unknown type. */
function inverseLabel(type) { return INVERSE[type] || 'related to'; }

/**
 * The list of valid type strings from an ontology object. Defaults to the in-code
 * ONTOLOGY; callers can pass a runtime-loaded Firestore ontology doc for the live vocab.
 * @param {Ontology} [ontology]
 * @returns {string[]}
 */
function getValidTypes(ontology = ONTOLOGY) {
  return ontology.types.map(t => t.type);
}

/**
 * Look up a single type entry by name. Returns undefined if not found.
 * @param {string} typeName
 * @param {Ontology} [ontology]
 * @returns {OntologyType | undefined}
 */
function getTypeEntry(typeName, ontology = ONTOLOGY) {
  return ontology.types.find(t => t.type === typeName);
}

/** The valid type strings, derived from ONTOLOGY (order-stable). */
const VALID_TYPES = getValidTypes();

module.exports = {
  ONTOLOGY,
  FORWARD,
  INVERSE,
  VALID_TYPES,
  forwardLabel,
  inverseLabel,
  getValidTypes,
  getTypeEntry,
};
